/**
 * First-party stash of ad click ids (?fbclid, ?ttclid, ?ScCid) captured from
 * the LANDING url at earliest script init — before SPA navigation can drop the
 * query string. The stash is never sent anywhere by itself; the analytics
 * client materializes cookies from it independently of browser-pixel consent,
 * using the stashed landing timestamp so a reconstructed _fbc carries the real
 * click time, never send time. Like the Meta identifier cookies, the stash
 * intentionally survives browser-pixel consent denial (see
 * clearConsentGatedMarketingIdentifierCookies in client.ts).
 *
 * Persisted in localStorage because the click window platforms attribute
 * against outlives a tab (a buyer who lands from an ad, closes the tab, and
 * purchases tomorrow is still an ad-click conversion). A tab-scoped stash
 * would lose the click ID exactly when the ITP-capped _fbc cookie has also
 * expired. Entries age out at the same 90 days fbevents.js keeps _fbc.
 */

const STASH_STORAGE_KEY = 'wm_click_id_stash';
const STASH_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const CLICK_ID_PATTERN = /^\S{1,450}$/;

export interface StashedClickId {
	id: string;
	ts: number;
}

export interface ClickIdStash {
	fbclid?: StashedClickId;
	ttclid?: StashedClickId;
	scclid?: StashedClickId;
}

export type ClickIdKind = keyof ClickIdStash;

let memoryStash: ClickIdStash | null = null;

export function captureCurrentUrlClickIds(persist: boolean) {
	if (typeof window === 'undefined') return;

	try {
		const landing = readLandingClickIds(window.location.search);
		if (!persist) {
			memoryStash = computeNextStash(memoryStash, landing, Date.now());
			return;
		}
		memoryStash = refreshClickIdStash(landing, Date.now());
		writeStoredStash(memoryStash);
	} catch {
		// Storage unavailable — the in-memory stash still covers this page.
	}
}

export function getStashedClickId(kind: ClickIdKind): StashedClickId | null {
	if (typeof window === 'undefined') return null;
	try {
		memoryStash = refreshClickIdStash({}, Date.now());
	} catch {
		// Storage unavailable — retain the current tab's in-memory stash.
	}
	const entry = memoryStash?.[kind];
	if (!entry || typeof entry.id !== 'string' || !Number.isFinite(entry.ts)) return null;
	return Date.now() - entry.ts <= STASH_MAX_AGE_MS ? entry : null;
}

/**
 * No production flow calls this today — consent withdrawal deliberately keeps
 * the stash (see the module comment). Exported for test isolation and as the
 * hook for a future explicit data-clear flow.
 */
export function clearClickIdStash() {
	memoryStash = null;
	try {
		window.localStorage.removeItem(STASH_STORAGE_KEY);
	} catch {
		// Storage unavailable.
	}
	try {
		window.sessionStorage.removeItem(STASH_STORAGE_KEY);
	} catch {
		// Storage unavailable.
	}
}

export function readLandingClickIds(search: string): Partial<Record<ClickIdKind, string>> {
	const params = new URLSearchParams(search);
	const ids: Partial<Record<ClickIdKind, string>> = {};

	const fbclid = normalizeClickId(params.get('fbclid'));
	if (fbclid) ids.fbclid = fbclid;

	const ttclid = normalizeClickId(params.get('ttclid'));
	if (ttclid) ids.ttclid = ttclid;

	// Snapchat documents the param as ScCid, but casing varies in the wild.
	for (const [key, value] of params) {
		const normalized = normalizeClickId(value);
		if (key.toLowerCase() === 'sccid' && normalized) {
			ids.scclid = normalized;
			break;
		}
	}

	return ids;
}

/**
 * A different id supersedes the stashed one (a new ad click); re-landing with
 * the SAME id keeps the original timestamp — the click happened once, and the
 * first capture is its real time. Ids absent from this landing keep their
 * stashed entries so a later TikTok click doesn't erase a stashed Meta click.
 * Entries older than the retention window are pruned on every capture.
 */
export function computeNextStash(
	previous: ClickIdStash | null,
	landing: Partial<Record<ClickIdKind, string>>,
	now: number,
): ClickIdStash {
	const next: ClickIdStash = {};

	for (const kind of ['fbclid', 'ttclid', 'scclid'] as const) {
		const stashed = previous?.[kind];
		const age = stashed ? now - stashed.ts : Number.NaN;
		if (
			stashed &&
			normalizeClickId(stashed.id) &&
			Number.isFinite(stashed.ts) &&
			age >= 0 &&
			age <= STASH_MAX_AGE_MS
		) {
			next[kind] = stashed;
		}

		const id = landing[kind];
		if (!id) continue;
		if (next[kind]?.id !== id) {
			next[kind] = { id, ts: now };
		}
	}

	return next;
}

export function mergeClickIdStashes(
	first: ClickIdStash | null,
	second: ClickIdStash | null,
): ClickIdStash {
	const merged: ClickIdStash = {};

	for (const kind of ['fbclid', 'ttclid', 'scclid'] as const) {
		const selected = selectMergedEntry(first?.[kind], second?.[kind]);
		if (selected) merged[kind] = selected;
	}

	return merged;
}

function selectMergedEntry(left: StashedClickId | undefined, right: StashedClickId | undefined) {
	if (!left) return right;
	if (!right) return left;
	if (left.id === right.id) return left.ts <= right.ts ? left : right;
	return left.ts >= right.ts ? left : right;
}

function refreshClickIdStash(landing: Partial<Record<ClickIdKind, string>>, now: number) {
	// Advance this tab independently first. Merging afterward means a stale tab
	// whose URL still contains its original click cannot overwrite a newer click
	// captured by another tab through shared localStorage.
	const currentTab = computeNextStash(memoryStash, landing, now);
	const stored = computeNextStash(readStoredStash(), {}, now);
	return mergeClickIdStashes(currentTab, stored);
}

function writeStoredStash(stash: ClickIdStash) {
	if (Object.keys(stash).length > 0) {
		window.localStorage.setItem(STASH_STORAGE_KEY, JSON.stringify(stash));
	} else {
		window.localStorage.removeItem(STASH_STORAGE_KEY);
	}
	// Remove the pre-localStorage copy after migration so it cannot reappear
	// when the canonical stash expires or is cleared.
	window.sessionStorage.removeItem(STASH_STORAGE_KEY);
}

function normalizeClickId(value: string | null | undefined) {
	const normalized = value?.trim();
	return normalized && CLICK_ID_PATTERN.test(normalized) ? normalized : null;
}

function readStoredStash(): ClickIdStash | null {
	// The sessionStorage read migrates stashes written before the switch to
	// localStorage, keeping in-flight sessions' click ids across a deploy.
	const stored =
		window.localStorage.getItem(STASH_STORAGE_KEY) ??
		window.sessionStorage.getItem(STASH_STORAGE_KEY);
	if (!stored) return null;
	const parsed = JSON.parse(stored) as ClickIdStash;
	return parsed && typeof parsed === 'object' ? parsed : null;
}
